// ... (imports will be handled by replacing the whole file or parts. I will replace the component content)

import DealsApi from "@apis/deals";
import Button from "@components/button";
import Fields from "@components/fields";
import DateTimePicker from "@components/fields/datetime";
import Wrapper from "@components/wrapper";
import Header from "@commons/header";
import Row from "@components/row";
import Column from "@components/column";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Module, Zone } from "@apis/index";
import DropDown from "@components/fields/dropdown";
import dayjs from "dayjs";

const validationSchema = Yup.object().shape({
  moduleId: Yup.string().required("Module is required"),
  zoneIds: Yup.array().min(1, "At least one zone is required").required("Zone is required"),
  title: Yup.string().required("Title is required"),
  type: Yup.string().required("Type is required"),
  priority: Yup.number().required("Priority is required"),
  startDate: Yup.date().nullable().required("Start date is required"),
  endDate: Yup.date().nullable().required("End date is required"),
  startTime: Yup.string().nullable().required("Start time is required"),
  endTime: Yup.string().nullable().required("End time is required"),
});

const DealsForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [modules, setModules] = useState<any[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const [initialValues, setInitialValues] = useState<any>({
    moduleId: "",
    zoneIds: [],
    title: "",
    type: "default",
    priority: 0,
    coverImage: "",
    startDate: null,
    endDate: null,
    startTime: "",
    endTime: "",
    isActive: true,
  });
  const [loading, setLoading] = useState(false);

  const fetchZones = async (moduleId: any) => {
    try {
      const zoneRes: any = await Zone.list({
        state: "zones",
        params: { moduleId: moduleId || undefined },
      });
      if (zoneRes) setZones(zoneRes);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const moduleRes: any = await Module.list({
          state: "modules",
          params: { isActive: true },
        });
        if (moduleRes) setModules(moduleRes);
        fetchZones("");
      } catch (error) {
        console.error("Error fetching dropdowns", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (id) {
      setLoading(true);
      DealsApi.find({ id }).then((res: any) => {
        if (res) {
          // Check format of startTime/endTime from backend. Assuming HH:mm:ss or HH:mm
          // We need to convert to 12h format for display if DateTimePicker helper expects that,
          // OR simply pass the value if DateTimePicker can parse it?
          // DateTimePicker `parseInputDate` handles "HH:mm:ss" properly?
          // It handles "HH:mm".
          const formatTime = (t: string) => {
            if (!t) return "";
            // If backend sends HH:mm:ss, take first 5 chars
            const [h, m] = t.split(":");
            if (!h || !m) return "";
            const hour = parseInt(h);
            const ampm = hour >= 12 ? "PM" : "AM";
            const hour12 = hour % 12 || 12;
            return `${hour12}:${m} ${ampm}`;
          };

          setInitialValues({
            moduleId: res.moduleId || "",
            zoneIds: res.zoneIds || (res.zoneId ? [res.zoneId] : []),
            title: res.title || "",
            type: res.type || "default",
            priority: res.priority || 0,
            isActive: res.isActive,
            startDate: res.startDate ? new Date(res.startDate) : null,
            endDate: res.endDate ? new Date(res.endDate) : null,
            startTime: res.startTime ? formatTime(res.startTime) : "",
            endTime: res.endTime ? formatTime(res.endTime) : "",
            coverImage: res.coverImage,
          });
          if (res.moduleId) {
            fetchZones(res.moduleId);
          }
        }
        setLoading(false);
      });
    }
  }, [id]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("moduleId", values.moduleId);
    if (values.zoneIds && values.zoneIds.length > 0) {
      values.zoneIds.forEach((id: any) => {
        formData.append("zoneIds[]", id);
      });
    }
    formData.append("title", values.title);
    formData.append("type", values.type);
    formData.append("priority", values.priority);
    formData.append("isActive", values.isActive);

    // Format dates to YYYY-MM-DD
    const formatDate = (d: any) => {
      if (!d) return "";
      try {
        return new Date(d).toISOString().split("T")[0];
      } catch (e) {
        return "";
      }
    };

    formData.append("startDate", formatDate(values.startDate));
    formData.append("endDate", formatDate(values.endDate));

    // Format times to HH:mm
    const formatTime = (t: string) => {
      if (!t) return "";
      // t is likely "hh:mm AM/PM"
      const match = t.match(/^(\d+):(\d+)\s?(AM|PM)$/i);
      if (match) {
        let [, h, m, modifier] = match;
        let hour = parseInt(h);
        if (modifier.toUpperCase() === "PM" && hour < 12) hour += 12;
        if (modifier.toUpperCase() === "AM" && hour === 12) hour = 0;
        return `${hour.toString().padStart(2, "0")}:${m}`;
      }
      return t; // Fallback
    };

    formData.append("startTime", formatTime(values.startTime));
    formData.append("endTime", formatTime(values.endTime));

    if (values.coverImage && values.coverImage.rawFile) {
      formData.append("coverImage", values.coverImage.rawFile);
    }

    try {
      if (id) {
        await DealsApi.update({ id, data: formData });
        toast.success("Updated successfully");
      } else {
        await DealsApi.create({ data: formData });
        toast.success("Created successfully");
      }
      navigate("/admin/deals");
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Failed to save deal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Header title={id ? "Edit Deal" : "New Deal"} />
      <br />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <Form>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "7fr 3fr",
                gap: "30px",
                alignItems: "start",
              }}
            >
              {/* Left Column: Main Info */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Wrapper>
                  <div style={{ marginBottom: "24px" }}>
                    <h3
                      style={{
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "#1e293b",
                        margin: "0 0 20px 0",
                      }}
                    >
                      General Information
                    </h3>
                    <Column stylecolumn="gap: 20px;">
                      <Fields.Input
                        name="title"
                        label="Deal Title"
                        placeholder="Enter deal title"
                      />
                      <Row styleRow="gap:20px;">
                        <DropDown
                          name="type"
                          label="Type"
                          options={[
                            { id: "default", name: "Default" },
                            { id: "offer", name: "Offer" },
                          ]}
                          titleAsKey="name"
                          valueAsKey="id"
                          placeholder="Select Type"
                          styleInput="width: 96%;"
                        />
                        <Fields.Input
                          name="priority"
                          label="Priority"
                          type="number"
                          placeholder="0"
                        />
                      </Row>
                      <Row styleRow="gap:20px;">
                        <DropDown
                          name="moduleId"
                          label="Module"
                          options={modules}
                          titleAsKey="name"
                          valueAsKey="id"
                          placeholder="Select Module"
                          onChange={(val) => {
                            setFieldValue("moduleId", val);
                            setFieldValue("zoneIds", []);
                            fetchZones(val);
                          }}
                          styleInput="width: 96%;"
                        />
                        <DropDown
                          name="zoneIds"
                          label="Zone"
                          options={zones}
                          titleAsKey="name"
                          valueAsKey="id"
                          placeholder="Select Zone"
                          isMultiple={true}
                          disabled={!values.moduleId}
                          styleInput="width: 94%;"
                        />
                      </Row>
                    </Column>
                  </div>
                </Wrapper>

                <Wrapper>
                  <div style={{ marginBottom: "24px" }}>
                    <h3
                      style={{
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "#1e293b",
                        margin: "0 0 20px 0",
                      }}
                    >
                      Schedule
                    </h3>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "24px",
                      }}
                    >
                      {/* Start Schedule */}
                      <div
                        style={{
                          padding: "20px",
                          backgroundColor: "#f8fafc",
                          borderRadius: "12px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "14px",
                            marginBottom: "16px",
                            color: "#475569",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: "#22c55e",
                            }}
                          ></span>
                          Start Schedule
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "20px",
                          }}
                        >
                          <DateTimePicker
                            name="startDate"
                            label="Start Date"
                            placeholder="Select date"
                            showDateSelect
                          />
                          <DateTimePicker
                            name="startTime"
                            label="Start Time"
                            placeholder="Select time"
                            showTimeSelect
                            showDateSelect={false}
                            dateFormat="h:mm aa"
                            timeFormat="hh:mm aa"
                          />
                        </div>
                      </div>

                      {/* End Schedule */}
                      <div
                        style={{
                          padding: "20px",
                          backgroundColor: "#fff1f2",
                          borderRadius: "12px",
                          border: "1px dotted #f43f5e",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "14px",
                            marginBottom: "16px",
                            color: "#be123c",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: "#e11d48",
                            }}
                          ></span>
                          End Schedule
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "20px",
                          }}
                        >
                          <DateTimePicker
                            name="endDate"
                            label="End Date"
                            placeholder="Select date"
                            showDateSelect
                          />
                          <DateTimePicker
                            name="endTime"
                            label="End Time"
                            placeholder="Select time"
                            showTimeSelect
                            showDateSelect={false}
                            dateFormat="h:mm aa"
                            timeFormat="hh:mm aa"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Wrapper>
              </div>

              {/* Right Column: Status & Media */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Wrapper>
                  <div style={{ marginBottom: "16px" }}>
                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#1e293b",
                        margin: "0 0 16px 0",
                      }}
                    >
                      Publish Status
                    </h3>
                    <div
                      style={{
                        padding: "16px",
                        backgroundColor: values.isActive
                          ? "#f0fdf4"
                          : "#f1f5f9",
                        borderRadius: "8px",
                        border: `1px solid ${values.isActive ? "#bbf7d0" : "#e2e8f0"}`,
                      }}
                    >
                      <Fields.Switch
                        name="isActive"
                        label={values.isActive ? "Active" : "Inactive"}
                      />
                    </div>
                  </div>
                </Wrapper>

                <Wrapper>
                  <div style={{ marginBottom: "16px" }}>
                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#1e293b",
                        margin: "0 0 16px 0",
                      }}
                    >
                      Deal Banner
                    </h3>
                    <Fields.Upload
                      name="coverImage"
                      label=""
                      placeholder="Upload deal banner"
                      useRawFile
                      mode={id ? "update" : "create"}
                      styleWrapper="width: 100%; aspect-ratio: 16/9;"
                    />
                  </div>
                </Wrapper>

                <Button
                  type="submit"
                  title={id ? "Update Deal" : "Create Deal"}
                  disabled={loading}
                  loading={loading}
                  styleButton="width: 100%; padding: 12px; font-weight: 600;"
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
};

export default DealsForm;
